import { useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { 
  LayoutDashboard, 
  FolderOpen, 
  Image, 
  MessageSquareQuote, 
  Building2, 
  Users, 
  LogOut,
  Loader2,
  Settings
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_843f7c3a-3692-4728-b14f-ba431557a9c8/artifacts/qxen4yga_Ray%20Creators%20-%20Logo.png";

const AdminLayout = ({ children }) => {
  const { isAuthenticated, loading, logout, admin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/getin');
    }
  }, [isAuthenticated, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const sidebarLinks = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/getin/dashboard', tab: 'dashboard' },
    { icon: FolderOpen, label: 'Categories', path: '/getin/dashboard?tab=categories', tab: 'categories' },
    { icon: Image, label: 'Projects', path: '/getin/dashboard?tab=projects', tab: 'projects' },
    { icon: MessageSquareQuote, label: 'Testimonials', path: '/getin/dashboard?tab=testimonials', tab: 'testimonials' },
    { icon: Building2, label: 'Client Logos', path: '/getin/dashboard?tab=logos', tab: 'logos' },
    { icon: Users, label: 'Admins', path: '/getin/dashboard?tab=admins', tab: 'admins' },
    { icon: Settings, label: 'Settings', path: '/getin/dashboard?tab=settings', tab: 'settings' },
  ];

  const currentTab = new URLSearchParams(location.search).get('tab') || 'dashboard';

  const handleLogout = () => {
    logout();
    navigate('/getin');
  };

  return (
    <div className="min-h-screen flex bg-muted">
      {/* Sidebar */}
      <aside className="w-64 admin-sidebar flex flex-col" data-testid="admin-sidebar">
        <div className="p-6 border-b border-white/10">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src={LOGO_URL} 
              alt="Ray Creators" 
              className="h-10 w-10 object-contain"
            />
            <div className="text-lg">
              <span className="font-serif text-white uppercase tracking-wider">Ray</span>
              <span className="font-script text-primary text-xl ml-1">Creators</span>
            </div>
          </Link>
        </div>

        <nav className="flex-1 p-4 space-y-2">
          {sidebarLinks.map((link) => (
            <Link
              key={link.tab}
              to={link.path}
              className={`admin-sidebar-link flex items-center gap-3 ${
                currentTab === link.tab ? 'active bg-primary/20' : ''
              }`}
              data-testid={`admin-nav-${link.tab}`}
            >
              <link.icon size={20} />
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="p-4 border-t border-white/10">
          <div className="mb-4 px-3 text-sm text-white/60">
            Logged in as <span className="text-white font-medium">{admin?.username}</span>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-white/80 hover:text-white hover:bg-destructive/20"
            onClick={handleLogout}
            data-testid="admin-logout-btn"
          >
            <LogOut size={20} className="mr-3" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className="p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
